import React, { SetStateAction, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { CheckSquare, Pen, Trash } from 'react-bootstrap-icons';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AuthService } from '../../../authentification/login/service';
import ConfirmationModal from '../../../shared/components/ConfirmationModal';
import EmptyTable from '../../../shared/components/EmptyTable';
import {
  EChangeTarget,
  EChangeTargetStatus,
} from '../../../shared/enums/EChangeTarget';
import { formatDate } from '../../../shared/functions/formatDate';
import { formatNumber } from '../../../shared/functions/formatNumber';
import {
  needsAlarmSystemUpdate,
  needsService,
  needsTachographUpdate,
  needsTechInspection,
} from '../../../shared/functions/getTruckReminder';
import { handleError } from '../../../shared/functions/handleError';
import { ITruck } from '../../../shared/interfaces/ITruck';
import { AdminTruckService } from '../service';
import TruckPreview from './TruckPreview';

interface IProps {
  trucks: ITruck[];
  isAdmin?: boolean;
  reset: UseFormReset<ITruck>;
  setIsActionModalOpened: React.Dispatch<SetStateAction<boolean>>;
  handleUpdateList: () => void;
}
const TruckTable = ({
  trucks,
  reset,
  isAdmin,
  setIsActionModalOpened,
  handleUpdateList,
}: IProps) => {
  const { t } = useTranslation();
  const [isPreviewModalOpened, setIsPreviewModalOpened] = useState(false);
  const [isDeleteOpened, setIsDeleteOpened] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState<ITruck | undefined>(
    undefined,
  );
  const handleCancelDelete = () => {
    setSelectedTruck(undefined);
    setIsDeleteOpened(false);
  };
  const handleConfirmDelete = async () => {
    !!selectedTruck?.id &&
      (await AdminTruckService.delete(selectedTruck?.id)
        .then((response) => {
          if (response) {
            handleUpdateList();
            handleCancelDelete();
            toast.success(
              t('toast.successDelete', { name: selectedTruck?.licensePlate }),
            );
          }
        })
        .catch(handleError));
  };
  const handleClosePreviewModal = () => {
    setSelectedTruck(undefined);
    setIsPreviewModalOpened(false);
  };
  const handleConfirmPreview = async (status: EChangeTargetStatus) => {
    await AuthService.updateChangeTarget({
      status,
      targetId: String(selectedTruck?.id),
      target: EChangeTarget.TRUCK,
    })
      .then((response) => {
        if (response) {
          handleUpdateList();
          handleClosePreviewModal();
          toast.success(
            t('toast.successUpdated', { name: selectedTruck?.licensePlate }),
          );
        }
      })
      .catch(handleError);
  };

  return (
    <>
      <div className="table-responsive">
        <table className="table ">
          <thead className="table-dark">
            <tr>
              <th scope="col" className={'vertical-align-middle'}>
                #
              </th>
              <th scope="col" className={'vertical-align-middle'}>
                {t('general.licensePlate')}
              </th>
              <th scope="col" className={'vertical-align-middle'}>
                {t('general.nextServiceKm')}
              </th>
              <th scope="col" className={'vertical-align-middle'}>
                {t('general.mileage')}
              </th>
              <th scope="col" className={'vertical-align-middle'}>
                {t('general.technicalInspection')}
              </th>
              <th scope="col" className={'vertical-align-middle'}>
                {t('general.alarmSystem')}
              </th>
              <th scope="col" className={'vertical-align-middle'}>
                {t('general.tachograph')}
              </th>
              <th scope="col" className={'vertical-align-middle'}>
                {t('general.actions')}
              </th>
              <th scope="col" className={'vertical-align-middle'}>
                {t('general.actions')}
              </th>
            </tr>
          </thead>
          <tbody className={'border'}>
            {!!trucks?.length ? (
              trucks?.map((truck) => {
                console.log(
                  needsTechInspection(truck),
                  truck.technicalInspection,
                  'aa',
                );
                return (
                  <tr key={truck?.id}>
                    <td
                      className={
                        truck?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      {truck?.id}
                    </td>
                    <td
                      className={
                        truck?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      {truck?.licensePlate}
                    </td>
                    <td
                      className={
                        truck?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      {formatNumber(truck?.nextServiceKm)}
                    </td>
                    <td
                      className={
                        truck?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      {formatNumber(truck?.mileage)}
                    </td>
                    <td
                      className={
                        truck?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      {formatDate(truck?.technicalInspection)}
                    </td>
                    <td
                      className={
                        truck?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      {formatDate(truck?.alarmSystem)}
                    </td>
                    <td
                      className={
                        truck?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      {formatDate(truck?.tachograph)}
                    </td>

                    <td
                      className={
                        truck?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      <div className="d-flex flex-wrap">
                        {needsService(truck) && (
                          <div className="badge bg-danger me-1 mb-1 truck-badge"></div>
                        )}
                        {needsTechInspection(truck) && (
                          <div className="badge bg-warning text-dark me-1 mb-1 truck-badge"></div>
                        )}
                        {needsAlarmSystemUpdate(truck) && (
                          <div className="badge bg-success text-dark me-1 mb-1 truck-badge"></div>
                        )}
                        {needsTachographUpdate(truck) && (
                          <div className="badge bg-secondary me-1 mb-1 truck-badge"></div>
                        )}
                      </div>
                    </td>
                    <td
                      className={
                        truck?.hasPendingChange ? 'bg-warning-subtle' : ''
                      }
                    >
                      <div className="d-flex align-items-center justify-content-center">
                        <Button
                          variant={'warning'}
                          size={'sm'}
                          onClick={async () => {
                            truck?.id &&
                              (await AdminTruckService.getSingle(truck?.id)
                                .then((response) => {
                                  reset({
                                    ...response,
                                    brand: undefined,
                                    brandId: response?.brand?.id,
                                    services: response?.services?.sort(
                                      (a, b) => Number(b?.id) - Number(a?.id),
                                    ),
                                  });
                                  setIsActionModalOpened(true);
                                })
                                .catch(handleError));
                          }}
                        >
                          <Pen />
                        </Button>
                        {isAdmin && (
                          <>
                            <Button
                              variant={'danger'}
                              className={'mx-2'}
                              size={'sm'}
                              onClick={() => {
                                setSelectedTruck({
                                  ...truck,
                                  brandId: Number(truck?.brand?.id),
                                });
                                setIsDeleteOpened(true);
                              }}
                            >
                              <Trash />
                            </Button>
                          </>
                        )}
                        {truck?.hasPendingChange && (
                          <Button
                            variant={'primary'}
                            className={'mx-2'}
                            size={'sm'}
                            onClick={() => {
                              setSelectedTruck({
                                ...truck,
                                brandId: Number(truck?.brand?.id),
                              });
                              setIsPreviewModalOpened(true);
                            }}
                          >
                            <CheckSquare />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <EmptyTable columns={9} />
            )}
          </tbody>
        </table>
      </div>
      <div className="mb-3">
        <span className="badge bg-danger me-2"> {t('general.serviceDue')}</span>
        <span className="badge bg-warning text-white me-2">
          {t('general.inspectionSoon')}
        </span>
        <span className="badge bg-success text-white me-2">
          {t('general.alarmExpired')}
        </span>
        <span className="badge bg-secondary me-2">
          {t('general.tachographExpired')}
        </span>
        <span className="badge bg-warning-subtle text-dark me-2">
          {t('general.confirmationMissing')}
        </span>
      </div>

      <ConfirmationModal
        name={selectedTruck?.licensePlate}
        isActionModalOpened={isDeleteOpened}
        handleConfirmDeletion={handleConfirmDelete}
        handleCloseActionModal={handleCancelDelete}
      />
      <Modal
        show={isPreviewModalOpened}
        onHide={handleClosePreviewModal}
        size={'lg'}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('general.preview')} "{selectedTruck?.licensePlate}"
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TruckPreview truckId={Number(selectedTruck?.id)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClosePreviewModal}>
            {t('button.close')}
          </Button>
          {isAdmin && (
            <>
              <Button
                variant="danger"
                onClick={() =>
                  handleConfirmPreview(EChangeTargetStatus.REJECTED)
                }
              >
                {t('button.decline')}
              </Button>
              <Button
                variant="primary"
                onClick={() =>
                  handleConfirmPreview(EChangeTargetStatus.APPROVED)
                }
              >
                {t('button.accept')}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TruckTable;
